import { useCallback, useEffect, useState } from 'react'
// @mui
import Stack from '@mui/material/Stack'
import MenuItem from '@mui/material/MenuItem'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputAdornment from '@mui/material/InputAdornment'
import Select, { SelectChangeEvent } from '@mui/material/Select'
// types
import { IUserTableFilters, IUserTableFilterValue } from 'src/types/user'
// components
import Iconify from 'src/components/iconify'
import { OptionType } from 'src/components/hook-form/rhf-autocomplete'

// ----------------------------------------------------------------------

type Props = {
  filters: IUserTableFilters
  onFilters: (name: string, value: IUserTableFilterValue) => void
  roleOptions: OptionType[]
}

export default function UserTableToolbar({ filters, onFilters, roleOptions }: Props) {
  const [query, setQuery] = useState(filters.search)
  const [debouncedQuery, setDebouncedQuery] = useState(filters.search)

  const handleFilterName = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }, [])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query)
    }, 500)

    return () => {
      clearTimeout(handler)
    }
  }, [query])

  useEffect(() => {
    if (debouncedQuery !== filters.search) {
      onFilters('search', debouncedQuery)
    }
  }, [debouncedQuery, filters.search, onFilters])

  const handleFilterRole = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      onFilters(
        'role',
        typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value,
      )
    },
    [onFilters],
  )

  return (
    <Stack
      spacing={2}
      alignItems={{ xs: 'flex-end', md: 'center' }}
      direction={{ xs: 'column', md: 'row' }}
      sx={{ p: 2.5 }}
    >
      <FormControl
        size='small'
        sx={{ flexShrink: 0, width: { xs: 1, md: 180 } }}
      >
        <InputLabel>Tipo</InputLabel>
        <Select
          multiple
          size='small'
          value={filters.role}
          onChange={handleFilterRole}
          input={<OutlinedInput label='Tipo' />}
          renderValue={(selected) =>
            selected.map((v) => {
              const opt = roleOptions.find((o) => o.value === v)
              return opt?.label || v
            }).join(', ')
          }
          MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
        >
          {roleOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox
                disableRipple
                size='small'
                checked={filters.role.includes(option.value)}
              />
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        size='small'
        value={query}
        onChange={handleFilterName}
        placeholder='Buscar por nome, email ou CPF...'
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <Iconify icon='eva:search-fill' sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  )
}
